// ---------------------------------------------------------------------------
// Folgen sehen, bevor man loslässt (PF 55): Beim Ziehen einer Szene auf der Zeitlinie bzw. beim Umsortieren im Manuskript rechnet
// Dispositio die Geschichte, wie sie danach wäre, und vergleicht ihre Widersprüche mit den jetzigen – was neu hinzukäme, was sich auflöst
// und welche Szenen über ihren Anker mitwandern. Nichts wird verhindert und nichts nachgezogen, was der Text nicht sagt. Jede Prüfung,
// die `checkConsistency` kennt, zeigt sich so von selbst.
// ---------------------------------------------------------------------------

import { checkConsistency, type Issue } from './consistency.js';
import { createCtx, type StoryCtx } from './ctx.js';
import { manuscriptOf } from './narrative.js';
import { isScene } from './scene.js';
import { placementOf } from './sceneTime.js';
import { dependentsOf, refreshedTime } from './textTime.js';
import { isDocked } from './timeline.js';
import type { SceneTime, Story, StoryNode } from './types.js';

export interface Consequences {
  /** Widersprüche, die danach neu wären */
  added: Issue[];
  /** Widersprüche, die sich danach auflösten */
  resolved: Issue[];
  /** Szenen, die über ihren Anker mitwandern (ihre Zeit ändert sich mit) */
  along: string[];
}

/** Die Szene mit neuer Zeit; angedockt rückt sie an die Stelle, die ihr auf ihrem Zeitstrahl entspricht (wie auf dem Server) */
function timed(ctx: StoryCtx, scene: StoryNode, time: SceneTime | null): StoryNode {
  const tl = isDocked(ctx, scene) ? ctx.nodes.get(scene.parentId!) : undefined;
  const place = tl?.timeline ? placementOf(tl.timeline, time) : null;
  return place ? { ...scene, time, kind: place.kind, at: place.at, from: place.from, to: place.to } : { ...scene, time };
}

const replaced = (story: Story, node: StoryNode): Story => ({ ...story, nodes: story.nodes.map((n) => (n.id === node.id ? node : n)) });

/**
 * Die Geschichte, wie sie mit der Zeit `time` der Szene `sceneId` wäre: abhängige Szenen ziehen nach wie beim Speichern (aus ihrem Text
 * bzw. mit festem Abstand, die Kette entlang). `along`: die Szenen, deren Zeit sich dabei ändert.
 */
export function withSceneTime(ctx: StoryCtx, sceneId: string, time: SceneTime | null): { ctx: StoryCtx; along: string[] } {
  const scene = ctx.nodes.get(sceneId);
  if (!scene || !isScene(scene)) return { ctx, along: [] };
  let c = createCtx(replaced(ctx.story, timed(ctx, scene, time)));
  const seen = new Set([sceneId]);
  const queue = [sceneId];
  const along: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const dep of dependentsOf(c, id)) {
      if (seen.has(dep.id)) continue;
      seen.add(dep.id);
      const next = refreshedTime(c, dep);
      if (!next) continue;
      c = createCtx(replaced(c.story, timed(c, dep, next)));
      along.push(dep.id);
      queue.push(dep.id);
    }
  }
  return { ctx: c, along };
}

/**
 * Die Geschichte mit einer Szene bzw. einem Kapitel an anderer Stelle im Manuskript: neuer Sortierschlüssel, bei einer Szene auch ein
 * anderes Kapitel (`chapterId: null`: nicht eingeordnet, ohne Stelle).
 */
export function withPlacement(ctx: StoryCtx, nodeId: string, patch: { chapterId?: string | null; rank?: string | null; sectionId?: string | null }): StoryCtx {
  const node = ctx.nodes.get(nodeId);
  if (!node) return ctx;
  const { sectionId, ...rest } = patch;
  // ein Kapitel in einen anderen Abschnitt (null: direkt ins Manuskript)
  const parentId = sectionId === undefined ? node.parentId : (sectionId ?? manuscriptOf(ctx)?.id ?? node.parentId);
  const next = { ...node, ...rest, parentId };
  return createCtx(replaced(ctx.story, isScene(node) && next.chapterId === null ? { ...next, rank: null } : next));
}

/** Widersprüche (Hinweise „Prüfen“) ohne die Verweise – die ändern sich beim Verschieben nicht; je Stand einmal gerechnet */
const cache = new WeakMap<StoryCtx, Issue[]>();
function contradictions(ctx: StoryCtx): Issue[] {
  let found = cache.get(ctx);
  if (!found) cache.set(ctx, (found = checkConsistency(ctx, { references: false }).filter((i) => i.severity === 'warn')));
  return found;
}

/** Was sich an den Widersprüchen ändert, wenn aus `before` `after` wird (`along`: mitwandernde Szenen, siehe `withSceneTime`) */
export function consequences(before: StoryCtx, after: StoryCtx, along: string[] = []): Consequences {
  const was = contradictions(before);
  const now = contradictions(after);
  const wasIds = new Set(was.map((i) => i.id));
  const nowIds = new Set(now.map((i) => i.id));
  return { added: now.filter((i) => !wasIds.has(i.id)), resolved: was.filter((i) => !nowIds.has(i.id)), along };
}
